<script setup lang="ts">
import PricingInputRow from '@/components/asicw/pricing/PricingInputRow.vue'
import ElCurrencyInput from '@/components/common/ElCurrencyInput.vue'
import RlDialog from '@/components/common/RlDialog.vue'
import RlErrorAlert from '@/components/common/RlErrorAlert.vue'
import { useMinerModelFamilies } from '@/composables/core/miner-model-families'
import { useModelFamilyPricings } from '@/composables/asicw/pricings'
import { validateIsPositive } from '@/helpers/ep/form-validators'
import {
  PricingUnit,
  type ICreateMinerFamilyPricingForm,
  type IMinerModelFamilyPricing,
  type IPricingFormCommon,
  type IPricingFormAdditionalCosts,
  type IPricingFormPartCosts,
  type PricingLine,
} from '@/types/asicw/pricings'
import type { FormInstance, FormRules } from 'element-plus'
import { isNumber, isObject, round } from 'lodash'
import { reactive, ref, toRaw, watch } from 'vue'

const open = defineModel<boolean>({ required: true })
const props = defineProps<{ source?: IMinerModelFamilyPricing }>()

enum Tab {
  General,
  Repairs,
  LaborAndShipping,
}
const activeTab = ref<Tab>(Tab.General)

const emptyPricingLine = () => structuredClone({ unit: PricingUnit.Dollars })

/**
 * View-specific implementation of the BE-postable form: adds unit to adders.
 */
type IViewCreateMinerFamilyPricingForm = Partial<IPricingFormCommon> &
  Partial<IPricingFormPartCosts> &
  Partial<IPricingFormAdditionalCosts> & {
    modular_facility_type_adder: PricingLine
    traditional_facility_type_adder: PricingLine

    immersion_cooling_type_adder: PricingLine
    water_cooling_type_adder: PricingLine
    air_cooling_type_adder: PricingLine

    third_party_firmware_adder: PricingLine
    overclocking_adder: PricingLine
  }

const emptyFormState = (): IViewCreateMinerFamilyPricingForm =>
  structuredClone({
    miner_model_family: undefined,

    wholesale_base_cost: undefined,

    modular_facility_type_adder: emptyPricingLine(),
    traditional_facility_type_adder: emptyPricingLine(),

    immersion_cooling_type_adder: emptyPricingLine(),
    water_cooling_type_adder: emptyPricingLine(),
    air_cooling_type_adder: emptyPricingLine(),

    third_party_firmware_adder: emptyPricingLine(),
    overclocking_adder: emptyPricingLine(),

    fan_basic_repair_cost: undefined,
    fan_replacement_cost: undefined,

    power_supply_basic_repair_cost: undefined,
    power_supply_advanced_repair_cost: undefined,
    power_supply_replacement_cost: undefined,

    control_board_basic_repair_cost: undefined,
    control_board_advanced_repair_cost: undefined,
    control_board_replacement_cost: undefined,

    hashboard_basic_repair_cost: undefined,
    hashboard_advanced_repair_cost: undefined,
    hashboard_replacement_cost: undefined,

    fan_labor_cost: undefined,
    fan_shipping_cost: undefined,

    power_supply_labor_cost: undefined,
    power_supply_shipping_cost: undefined,

    control_board_labor_cost: undefined,
    control_board_shipping_cost: undefined,

    hashboard_labor_cost: undefined,
    hashboard_shipping_cost: undefined,
  })

/**
 * Stateless. Derives the form state from the source IMinerModelFamilyPricing.
 */
const initialUpdateFormState = (src: IMinerModelFamilyPricing): IViewCreateMinerFamilyPricingForm =>
  toRaw({
    // Common
    miner_model_family: src.miner_model_family,
    miner_model_family_abbreviation: src.miner_model_family_abbreviation,
    wholesale_base_cost: src.wholesale_base_cost,

    // Adders
    modular_facility_type_adder: { unit: PricingUnit.Dollars, dollarValue: src.modular_facility_type_adder },
    traditional_facility_type_adder: { unit: PricingUnit.Dollars, dollarValue: src.traditional_facility_type_adder },

    immersion_cooling_type_adder: { unit: PricingUnit.Dollars, dollarValue: src.immersion_cooling_type_adder },
    water_cooling_type_adder: { unit: PricingUnit.Dollars, dollarValue: src.water_cooling_type_adder },
    air_cooling_type_adder: { unit: PricingUnit.Dollars, dollarValue: src.air_cooling_type_adder },

    third_party_firmware_adder: { unit: PricingUnit.Dollars, dollarValue: src.third_party_firmware_adder },
    overclocking_adder: { unit: PricingUnit.Dollars, dollarValue: src.overclocking_adder },

    // Parts repair costs
    ...src.repair_costs,

    // Parts shipping and labor costs
    ...src.additional_costs,
  })

const form = reactive<IViewCreateMinerFamilyPricingForm>(emptyFormState())

const rules = reactive<FormRules<IViewCreateMinerFamilyPricingForm>>({
  // Can't manage to pickup select v2's blur event, not even from the component's @blur
  miner_model_family: { required: true, message: 'Please pick a model family', trigger: ['blur'] },
  wholesale_base_cost: { validator: validateIsPositive, message: 'Please enter wholesale base cost', trigger: 'blur' },
})

const formRef = ref<FormInstance>()

const { minerModelFamilyOptions } = useMinerModelFamilies()

// Update mutation
const { createFamilyPricingsMutation, updateModelFamilyPricingMutation } = useModelFamilyPricings()
const { mutate: createPricing, isError: isCreatePricingError, error: createPricingError } = createFamilyPricingsMutation
const {
  mutate: updatePricing,
  isError: isUpdatePricingError,
  error: updatePricingError,
} = updateModelFamilyPricingMutation
const isPending = ref(false)

/**
 * Fills in all undefined with zeroes, rounds computed ones, enforces type
 * @param form
 */
const prepareForm = (form: IViewCreateMinerFamilyPricingForm): ICreateMinerFamilyPricingForm =>
  Object.fromEntries(
    Object.entries(form).map(([k, v]) => {
      // If it's undefined/null, set a zero
      if (!v) return [k, 0]

      // If it's a number, round it up
      if (isNumber(v)) return [k, round(v, 2)]

      // If it's an object (i.e. an adder) extract out and round the dollar value, or set a zero if it's undefined/null
      if (isObject(v) && v.dollarValue) return [k, round(v.dollarValue, 2)]
      if (isObject(v) && !v.dollarValue) return [k, 0]

      // Else return as-is
      return [k, v]
    })
  ) as unknown as ICreateMinerFamilyPricingForm

const addModelFamilyPricing = () => {
  if (!form.miner_model_family || !form.wholesale_base_cost) return
  const payloadForm = prepareForm(form)

  isPending.value = true
  createPricing(payloadForm, {
    onError: () => {
      isPending.value = false
    },
    onSuccess: () => {
      open.value = false
    },
  })
}

const updateModelFamilyPricing = () => {
  if (!form.miner_model_family || !form.wholesale_base_cost || !props.source || !props.source.id) return
  const payloadForm = prepareForm(form)

  isPending.value = true
  updatePricing(
    { id: props.source.id, form: payloadForm },
    {
      onError: () => {
        isPending.value = false
      },
      onSuccess: () => {
        open.value = false
      },
    }
  )
}

const onAction = () => {
  if (props.source) updateModelFamilyPricing()
  else addModelFamilyPricing()
}

watch(
  () => props.source,
  () => {
    // Sync with source in case we're going for an update
    if (props.source) Object.assign(form, initialUpdateFormState(props.source))
    else Object.assign(form, emptyFormState())
  }
)

const onClosed = () => {
  isPending.value = false
  activeTab.value = Tab.General
  Object.assign(form, emptyFormState())
  formRef.value?.resetFields()
  formRef.value?.clearValidate()
  createFamilyPricingsMutation.reset()
  updateModelFamilyPricingMutation.reset()
}
</script>

<template>
  <RlDialog v-model="open" title="Add Model Family Pricing" width="592" @closed="onClosed">
    <div class="p-6 w-full flex flex-col gap-8">
      <el-radio-group v-model="activeTab" size="large" fill="var(--fill-tertiary)">
        <el-radio-button label="General" :value="Tab.General" />
        <el-radio-button label="Repairs & Replacement" :value="Tab.Repairs" />
        <el-radio-button label="Labor & Shipping" :value="Tab.LaborAndShipping" />
      </el-radio-group>

      <RlErrorAlert v-if="isCreatePricingError" :error="createPricingError" />
      <RlErrorAlert v-if="isUpdatePricingError" :error="updatePricingError" />

      <!-- General form -->
      <el-form
        v-if="activeTab == Tab.General"
        label-position="left"
        class="flex flex-col gap-6"
        :rules="rules"
        :model="form"
        hide-required-asterisk
      >
        <el-form-item label="Model Family" prop="miner_model_family">
          <el-select-v2
            v-if="minerModelFamilyOptions"
            v-model="form.miner_model_family"
            :options="minerModelFamilyOptions"
            filterable
            placeholder="Select model family"
            clearable
          />
        </el-form-item>

        <!-- Base cost -->
        <el-form-item label="Wholesale Cost Base" prop="wholesale_base_cost">
          <ElCurrencyInput v-model="form.wholesale_base_cost" :min-value="0" />
        </el-form-item>

        <!-- Facility type section -->
        <el-form-item label="Facility type">
          <el-form-item label="MDC" label-position="top">
            <PricingInputRow
              ref="modularFacilityTypeAdderRef"
              v-model="form.modular_facility_type_adder"
              :base-price="form.wholesale_base_cost ?? 0"
            />
          </el-form-item>

          <el-form-item label="Traditional DC" label-position="top">
            <PricingInputRow
              ref="traditionalFacilityTypeAdderRef"
              v-model="form.traditional_facility_type_adder"
              :base-price="form.wholesale_base_cost ?? 0"
            />
          </el-form-item>
        </el-form-item>

        <!-- Cooling method section -->
        <el-form-item label="Cooling method">
          <el-form-item label="Air" label-position="top">
            <PricingInputRow
              ref="airCoolingTypeAdderRef"
              v-model="form.air_cooling_type_adder"
              :base-price="form.wholesale_base_cost ?? 0"
            />
          </el-form-item>

          <el-form-item label="Water" label-position="top">
            <PricingInputRow
              ref="waterCoolingTypeAdderRef"
              v-model="form.water_cooling_type_adder"
              :base-price="form.wholesale_base_cost ?? 0"
            />
          </el-form-item>

          <el-form-item label="Immersion" label-position="top">
            <PricingInputRow
              ref="immersionCoolingTypeAdderRef"
              v-model="form.immersion_cooling_type_adder"
              :base-price="form.wholesale_base_cost ?? 0"
            />
          </el-form-item>
        </el-form-item>

        <!-- Third party software section -->
        <el-form-item label="3P Firmware">
          <PricingInputRow
            ref="thirdPartyFirmwareAdderRef"
            v-model="form.third_party_firmware_adder"
            :base-price="form.wholesale_base_cost ?? 0"
          />
        </el-form-item>

        <!-- Overclocking section -->
        <el-form-item label="Overclocking">
          <PricingInputRow
            ref="overclockingAdderRef"
            v-model="form.overclocking_adder"
            :base-price="form.wholesale_base_cost ?? 0"
          />
        </el-form-item>
      </el-form>

      <!-- Parts form -->
      <el-form v-if="activeTab == Tab.Repairs" class="flex flex-col gap-6">
        <div class="grid grid-cols-4 gap-y-6 gap-x-2">
          <p class="grid-col-start-2">Basic repair</p>
          <p>Advanced repair</p>
          <p>Replacement</p>
          <p>Fan</p>
          <ElCurrencyInput v-model="form.fan_basic_repair_cost" />
          <ElCurrencyInput v-model="form.fan_replacement_cost" class="grid-col-start-4" />
          <p>Hashboard</p>
          <ElCurrencyInput v-model="form.hashboard_basic_repair_cost" />
          <ElCurrencyInput v-model="form.hashboard_advanced_repair_cost" />
          <ElCurrencyInput v-model="form.hashboard_replacement_cost" />
          <p>Control Board</p>
          <ElCurrencyInput v-model="form.control_board_basic_repair_cost" />
          <ElCurrencyInput v-model="form.control_board_advanced_repair_cost" />
          <ElCurrencyInput v-model="form.control_board_replacement_cost" />
          <p>Power Supply</p>
          <ElCurrencyInput v-model="form.power_supply_basic_repair_cost" />
          <ElCurrencyInput v-model="form.power_supply_advanced_repair_cost" />
          <ElCurrencyInput v-model="form.power_supply_replacement_cost" />
        </div>
      </el-form>

      <!-- Labor and Shipping form -->
      <el-form v-if="activeTab == Tab.LaborAndShipping" class="flex flex-col gap-6">
        <div class="grid grid-cols-3 gap-y-6 gap-x-2">
          <p class="grid-col-start-2">Labor</p>
          <p>Shipping</p>
          <p>Fan</p>
          <ElCurrencyInput v-model="form.fan_labor_cost" />
          <ElCurrencyInput v-model="form.fan_shipping_cost" />
          <p>Hashboard</p>
          <ElCurrencyInput v-model="form.hashboard_labor_cost" />
          <ElCurrencyInput v-model="form.hashboard_shipping_cost" />
          <p>Control Board</p>
          <ElCurrencyInput v-model="form.control_board_labor_cost" />
          <ElCurrencyInput v-model="form.control_board_shipping_cost" />
          <p>Power Supply</p>
          <ElCurrencyInput v-model="form.power_supply_labor_cost" />
          <ElCurrencyInput v-model="form.power_supply_shipping_cost" />
        </div>
      </el-form>
    </div>
    <template #footer>
      <el-button size="large" @click="open = false">Cancel</el-button>
      <el-button
        size="large"
        type="primary"
        :disabled="!form.miner_model_family || !form.wholesale_base_cost"
        :loading="isPending"
        @click="onAction"
        >{{ source ? 'Update' : 'Add' }}</el-button
      >
    </template>
  </RlDialog>
</template>

<style scoped lang="scss">
:deep() {
  // Design's top radio buttons look and feel
  .el-radio-group {
    background-color: var(--component-fill-primary);
    border-radius: 8px;
    padding: 4px;

    .el-radio-button {
      &__inner {
        border: none;
        border-radius: 4px;
        height: 28px;
        padding: 0;
        display: flex;
        align-items: center;
        justify-content: center;
        color: var(--text-secondary) !important;
        font-weight: 400;
      }
    }
  }

  // Design's look for select placeholder text
  .el-select__placeholder.is-transparent {
    color: var(--text-placeholder);
  }

  // Replicate design's dimensions
  .el-form-item {
    margin-bottom: 0;

    &__label {
      color: var(--text-component-label);
      width: 160px;
    }

    &__content {
      width: 376;
    }

    // Match gap between form items in the same section
    &--label-left {
      .el-form-item__content {
        display: flex;
        flex-direction: column;
        gap: 24px;
      }
    }
  }

  .el-input {
    // Border radius
    &__wrapper {
      border-radius: 6px;
    }

    // Placeholder text color
    svg path,
    input::placeholder {
      color: var(--text-placeholder) !important;
    }
  }

  .grid p {
    color: var(--text-component-label);
  }
}
</style>
